/**
 * Libellé accordé au nombre `{n}` : une forme par catégorie de `Intl.PluralRules`
 * (français : one, other ; arabe : zero, one, two, few, many, other).
 */
export type Plural = Partial<Record<Intl.LDMLPluralRule, string>> & { other: string };

// Textes visibles : pas de tiret cadratin, pas de nom de fournisseur d'infrastructure.

export const fr = {
  appName: 'Beta Assur',
  tagline: 'Gestion des échéances et des rappels de primes',
  common: {
    loading: 'Chargement…',
    retry: 'Réessayer',
    save: 'Enregistrer',
    saved: 'Enregistré',
    cancel: 'Annuler',
    confirm: 'Confirmer',
    create: 'Créer',
    edit: 'Modifier',
    delete: 'Supprimer',
    close: 'Fermer',
    search: 'Rechercher',
    filter: 'Filtrer',
    reset: 'Réinitialiser',
    all: 'Tous',
    none: 'Aucun',
    yes: 'Oui',
    no: 'Non',
    loadMore: 'Afficher plus',
    empty: 'Aucun élément à afficher.',
    actions: 'Actions',
    back: 'Retour',
    details: 'Détails',
    required: 'Champ obligatoire',
    forbidden: 'Vous n’avez pas les droits nécessaires pour cette page.',
    notFound: 'Page introuvable.',
    errorTitle: 'Une erreur est survenue',
    requestId: 'Référence de la requête',
    from: 'Du',
    to: 'Au',
    language: 'Langue',
    sovereign: 'Données hébergées sur une infrastructure souveraine.',
  },
  nav: {
    label: 'Navigation principale',
    dashboard: 'Tableau de bord',
    installments: 'Échéances',
    portfolio: 'Contrats et assurés',
    scenarios: 'Scénarios et modèles',
    worklist: 'File de travail',
    imports: 'Imports et exports',
    reports: 'Rapports',
    cash: 'Caisse',
    settings: 'Paramètres',
    logout: 'Se déconnecter',
    skipToContent: 'Aller au contenu',
    openMenu: 'Ouvrir le menu',
    collapse: 'Réduire le menu',
    expand: 'Déplier le menu',
  },
  theme: {
    label: 'Thème',
    light: 'Clair',
    dark: 'Sombre',
    system: 'Comme le système',
  },
  account: {
    title: 'Mon compte',
    display: 'Affichage',
    compactMenu: 'Menu latéral réduit (icônes seules)',
    savedOnAccount:
      'Vos préférences sont enregistrées sur votre compte et vous suivent sur tous vos appareils.',
    profile: 'Profil',
    name: 'Nom affiché',
    role: 'Rôle',
    tenant: 'Cabinet',
    password: 'Mot de passe',
    currentPassword: 'Mot de passe actuel',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirmer le nouveau mot de passe',
    passwordHint: '12 caractères au moins ; une phrase est plus sûre.',
    changePassword: 'Changer le mot de passe',
    passwordChanged: 'Mot de passe changé. Vos autres sessions ont été fermées.',
    mismatch: 'Les deux nouveaux mots de passe ne correspondent pas.',
  },
  roles: {
    admin: 'Administrateur',
    manager: 'Gestionnaire',
    agent: 'Agent',
    readonly: 'Lecture seule',
  },
  auth: {
    sso: {
      or: 'ou',
      button: 'Se connecter avec Beta Suite',
      tenantRequired: 'Saisissez d’abord le code cabinet.',
      echec: 'La connexion Beta Suite a échoué. Réessayez ou utilisez votre mot de passe.',
      mfa: 'Votre compte exige une authentification forte : activez-la sur Beta Suite ou connectez-vous avec votre mot de passe et votre code.',
      indisponible: 'La connexion Beta Suite n’est pas activée pour ce cabinet.',
    },
    loginTitle: 'Connexion',
    tenantCode: 'Code cabinet',
    email: 'Adresse email',
    password: 'Mot de passe',
    totp: 'Code de vérification (6 chiffres)',
    totpHint: 'Saisissez le code affiché par votre application d’authentification.',
    submit: 'Se connecter',
    invalid: 'Identifiants invalides.',
    locked: 'Trop de tentatives. Réessayez dans quelques minutes.',
    invitationTitle: 'Activer votre compte',
    invitationIntro: 'Choisissez votre mot de passe pour accéder au portail.',
    passwordConfirm: 'Confirmer le mot de passe',
    passwordMismatch: 'Les deux mots de passe diffèrent.',
    passwordPolicy: '12 caractères au moins. Une phrase de passe est recommandée.',
    activate: 'Activer le compte',
    activated: 'Compte activé. Vous pouvez vous connecter.',
    goToLogin: 'Aller à la connexion',
    missingToken: 'Lien d’invitation incomplet.',
  },
  status: {
    upcoming: 'À venir',
    due: 'Échue',
    overdue: 'En retard',
    partial: 'Partiel',
    paid: 'Payée',
    waived: 'Abandonnée',
    cancelled: 'Annulée',
  },
  contractStatus: {
    active: 'En cours',
    suspended: 'Suspendu',
    lapsed: 'Déchu',
    cancelled: 'Résilié',
    expired: 'Expiré',
  },
  sequenceStatus: {
    scheduled: 'Planifiée',
    running: 'En cours',
    paused: 'En pause',
    stopped_paid: 'Arrêtée (payé)',
    stopped_optout: 'Arrêtée (opposition)',
    stopped_manual: 'Arrêtée',
    escalated: 'Escaladée',
    completed: 'Terminée',
  },
  channels: { whatsapp: 'WhatsApp', sms: 'SMS', email: 'Email' },
  paymentMethods: {
    kashy: 'Paiement en ligne',
    agency: 'En agence',
    bank: 'Virement',
    check: 'Chèque',
    other: 'Autre',
  },
  languages: { fr: 'Français', ar: 'Arabe' },
  periodicity: {
    annual: 'Annuelle',
    semiannual: 'Semestrielle',
    quarterly: 'Trimestrielle',
    monthly: 'Mensuelle',
    single: 'Unique',
  },
  families: {
    auto: 'Automobile',
    health: 'Santé',
    life: 'Vie et épargne',
    home: 'Habitation',
    business: 'Entreprises',
    other: 'Autre',
  },
  dashboard: {
    alertLabels: {
      draft_mode: 'Mode brouillon actif : aucun message n’est envoyé aux assurés.',
      missing_phone: {
        one: '{n} assuré sans numéro joignable.',
        other: '{n} assurés sans numéro joignable.',
      } as Plural,
      failed_messages: {
        one: '{n} message non délivré sur 7 jours.',
        other: '{n} messages non délivrés sur 7 jours.',
      } as Plural,
      import_failed: {
        one: '{n} import en échec sur 7 jours.',
        other: '{n} imports en échec sur 7 jours.',
      } as Plural,
      sending_suspended:
        'Envois suspendus après trop d’échecs : vérifier puis réactiver dans les paramètres.',
      templates_rejected: {
        one: '{n} modèle refusé à l’approbation WhatsApp.',
        other: '{n} modèles refusés à l’approbation WhatsApp.',
      } as Plural,
      orphan_payments: {
        one: '{n} paiement à affecter.',
        other: '{n} paiements à affecter.',
      } as Plural,
    },
    title: 'Tableau de bord',
    dueNext30: 'Reste à encaisser (30 jours)',
    recovered30: 'Encaissé (30 jours)',
    paidBeforeDueRate: 'Payées à temps (90 jours)',
    overdueOver15: 'Impayés de plus de 15 jours',
    activeSequences: 'Séquences actives',
    messagesThisMonth: 'Messages du mois',
    messagesCost: 'coût',
    dueToday: 'Échéances du jour',
    openEscalations: 'Escalades ouvertes',
    lastPayments: 'Derniers paiements',
    alerts: 'Alertes',
    noAlert: 'Aucune alerte.',
    draftBanner: 'Mode brouillon : les relances sont simulées, aucun message ne part.',
    chart: 'Dû et récupéré par semaine (12 semaines)',
    due: 'Dû',
    recovered: 'Récupéré',
    weekOf: 'Semaine du',
  },
  installments: {
    title: 'Échéances',
    policy: 'Police',
    policyholder: 'Assuré',
    product: 'Produit',
    dueDate: 'Échéance',
    amountDue: 'Montant dû',
    amountPaid: 'Payé',
    status: 'Statut',
    sequence: 'Relance',
    nextRun: 'Prochain envoi',
    agent: 'Agent',
    step: 'étape',
    minAmount: 'Montant min. (DT)',
    maxAmount: 'Montant max. (DT)',
    channel: 'Canal préféré',
    sequenceStatus: 'État de la relance',
    noPhone: 'Sans numéro joignable',
    highRisk: 'Risque d’impayé élevé',
    risk: 'Risque',
    riskFactors: {
      late_history: 'paie souvent en retard',
      very_late_history: 'retards longs',
      currently_overdue: 'en retard',
      no_history: 'sans historique',
      unreachable: 'injoignable',
      opted_out: 'opposé aux messages',
      delivery_failures: 'messages non délivrés',
    },
    insurer: 'Assureur',
    detailTitle: 'Échéance',
    period: 'Période couverte',
    graceUntil: 'Fin du délai de grâce',
    suspensionAt: 'Suspension prévue',
    source: 'Origine',
    sources: { computed: 'Calculée', imported: 'Importée', api: 'API' },
    timeline: 'Chronologie',
    noTimeline: 'Aucun événement pour cette échéance.',
    timelineKinds: {
      sequence: 'Relance',
      message: 'Message',
      payment: 'Paiement',
      audit: 'Action',
    },
    openContract: 'Voir le contrat',
    openPolicyholder: 'Voir l’assuré',
  },
  cash: {
    title: 'Journal de caisse',
    date: 'Jour de saisie',
    mine: 'Vos encaissements saisis ce jour.',
    team: 'Encaissements saisis par toute l’équipe ce jour.',
    totals: 'Totaux par mode de paiement',
    total: 'Total encaissé',
    payments: { one: '{n} paiement', other: '{n} paiements' } as Plural,
    none: 'Aucun encaissement saisi ce jour.',
    time: 'Heure',
    method: 'Mode',
    reference: 'Référence',
    amount: 'Montant',
    print: 'Imprimer',
  },
  mandate: {
    title: 'Prélèvement automatique',
    none: 'Aucun prélèvement : l’assuré peut le mettre en place depuis sa page.',
    cap: 'Plafond par prélèvement',
    since: 'actif depuis le',
    cancel: 'Arrêter le prélèvement',
    cancelMessage: 'Les échéances suivantes ne seront plus prélevées ; les rappels reprennent.',
    installment: 'Échéance',
    statuses: {
      pending: 'En attente de signature',
      active: 'Actif',
      cancelled: 'Arrêté',
      failed: 'Refusé',
    },
    chargeStatuses: { pending: 'En cours', succeeded: 'Prélevé', failed: 'Refusé' },
  },
  claims: {
    title: 'Sinistres déclarés',
    none: 'Aucun sinistre déclaré.',
    declare: 'Enregistrer une déclaration',
    kind: 'Nature du sinistre',
    kinds: {
      accident: 'Accident',
      theft: 'Vol',
      fire: 'Incendie',
      water_damage: 'Dégât des eaux',
      glass_breakage: 'Bris de glace',
      other: 'Autre',
    },
    occurredOn: 'Date du sinistre',
    place: 'Lieu',
    description: 'Circonstances',
    noHealth: 'Aucune information de santé : uniquement les circonstances matérielles.',
    thirdParty: 'Un tiers est impliqué',
    withThirdParty: 'avec tiers',
    save: 'Enregistrer',
    status: 'Suivi',
    statuses: { received: 'Reçue', forwarded: 'Transmise à l’assureur', closed: 'Close' },
  },
  renewal: {
    title: 'Renouvellement',
    none: 'Aucune proposition de renouvellement.',
    premium: 'Prime annuelle proposée (DT)',
    validUntil: 'Réponse attendue avant le',
    validUntilHint: 'Par défaut, la date d’expiration du contrat.',
    propose: 'Proposer le renouvellement',
    invalid: 'Prime invalide : montant en dinars (ex. 480,000).',
    cancel: 'Retirer',
    cancelMessage: 'L’assuré ne pourra plus répondre à cette proposition.',
    statuses: {
      pending: 'En attente',
      accepted: 'Acceptée',
      declined: 'Refusée',
      cancelled: 'Retirée',
    },
  },
  coverage: {
    title: 'Garanties',
    attestation: 'Attestation de paiement (PDF)',
    none: 'Aucune garantie détaillée pour ce contrat.',
    code: 'Code',
    label: 'Libellé',
    sumInsured: 'Capital assuré (DT)',
    premiumPart: 'Part de prime (DT)',
    add: 'Ajouter une garantie',
    invalid: 'Saisie invalide : code, libellé et montants en dinars (ex. 1250,500).',
    noHealthData: 'Ne saisir aucune donnée de santé : seuls le nom de la garantie et ses montants.',
  },
  endorsements: {
    title: 'Avenants',
    none: 'Aucun avenant.',
    currentPremium: 'Prime annuelle en vigueur',
    kind: 'Nature',
    kinds: {
      premium_change: 'Changement de prime',
      coverage_change: 'Changement de garanties',
      object_change: 'Changement de l’objet assuré',
      address_change: 'Changement d’adresse',
      other: 'Autre',
    },
    effectiveDate: 'Date d’effet',
    delta: 'Variation de la prime annuelle (DT)',
    deltaHint: 'Négative pour une baisse (ex. -50). Les échéances futures sont recalculées.',
    note: 'Commentaire',
    add: 'Enregistrer l’avenant',
    saved: 'Avenant enregistré, échéancier recalculé.',
  },
  portfolio: {
    saveNotices: 'Enregistrer les préavis',
    notices: 'Préavis',
    inheritNotice: 'Réglage de la branche',
    title: 'Contrats et assurés',
    contracts: 'Contrats',
    policyholders: 'Assurés',
    searchContracts: 'Numéro de police',
    searchPolicyholders: 'Nom, téléphone, CIN ou email',
    searchHint: '2 caractères au moins. Téléphone, CIN et email : valeur exacte.',
    policyNumber: 'Police',
    status: 'Statut',
    effectiveDate: 'Effet',
    expiryDate: 'Expiration',
    periodicity: 'Périodicité',
    premium: 'Prime annuelle',
    insuredObject: 'Objet assuré',
    insurer: 'Assureur',
    product: 'Produit',
    agent: 'Agent',
    noAgent: 'Sans agent',
    schedule: 'Échéancier',
    statusHistory: 'Historique des statuts',
    changeStatus: 'Changer le statut',
    newStatus: 'Nouveau statut',
    reason: 'Motif',
    statusChanged: 'Statut modifié.',
    newContract: 'Nouveau contrat',
    newPolicyholder: 'Nouvel assuré',
    kind: 'Type',
    person: 'Particulier',
    company: 'Entreprise',
    firstName: 'Prénom',
    lastName: 'Nom',
    companyName: 'Raison sociale',
    externalRef: 'Référence SI',
    phone: 'Téléphone',
    phoneVerified: 'vérifié',
    email: 'Email',
    cin: 'CIN',
    preferredChannel: 'Canal préféré',
    language: 'Langue des messages',
    optOut: 'Opposition aux messages',
    optOutSince: 'depuis le',
    noOptOut: 'Aucune opposition',
    contact: 'Contact et préférences',
    contractsOf: 'Contrats de l’assuré',
    messages: 'Journal des messages',
    noMessages: 'Aucun message.',
    sequenceNo: 'N°',
  },
  scenarios: {
    offsetFrom: 'Compté depuis',
    fromDue: 'l’échéance',
    fromNotice: 'le préavis',
    preferredFirst: 'Canal préféré de l’assuré d’abord',
    title: 'Scénarios et modèles',
    applyToRunning:
      'Appliquer aussi aux séquences en cours (elles reprennent à l’étape équivalente de la nouvelle version)',
    simulation: 'Simulation sur le portefeuille',
    simulate: 'Simuler',
    simInstallments: 'Échéances concernées',
    simMessages: 'Messages prévus',
    simEscalations: 'Escalades prévues',
    simNotStarted: 'Échéances sans séquence',
    simCost: 'Coût estimé',
    simCalendar: 'Messages prévus par jour',
    scenarios: 'Scénarios',
    templates: 'Modèles',
    installLibrary: 'Installer la bibliothèque par défaut',
    libraryInstalled: 'Bibliothèque installée',
    newScenario: 'Nouveau scénario',
    name: 'Nom',
    family: 'Famille de produits',
    anyFamily: 'Toutes familles',
    isDefault: 'Par défaut',
    status: 'État',
    statuses: { draft: 'Brouillon', published: 'Publié', archived: 'Archivé' },
    version: 'Version',
    published: 'version publiée',
    hasDraft: 'Brouillon en cours',
    steps: 'Étapes',
    addMessage: 'Ajouter un message',
    addEscalation: 'Ajouter une escalade',
    offset: 'Jour (par rapport à l’échéance)',
    kind: 'Type',
    kinds: { message: 'Message', escalation: 'Escalade' },
    channel: 'Canal',
    template: 'Modèle',
    fallback: 'Secours',
    noFallback: 'Sans secours',
    fallbackTemplate: 'Modèle de secours',
    label: 'Libellé',
    condition: 'Condition',
    noCondition: 'Toujours',
    conditionStatus: 'Seulement si l’échéance est',
    moveUp: 'Monter',
    moveDown: 'Descendre',
    removeStep: 'Retirer l’étape',
    stopOnPayment: 'Arrêter au paiement',
    pauseOnPromise: 'Pause sur promesse (jours)',
    publish: 'Publier',
    publishConfirm: 'Publier cette version ? Les séquences en cours gardent leur version.',
    duplicate: 'Dupliquer',
    archive: 'Archiver',
    archiveConfirm: 'Archiver ce scénario ?',
    preview: 'Aperçu de la chronologie',
    previewDueDate: 'Pour une échéance au',
    plannedAt: 'Envoi prévu',
    day: 'J',
    newTemplate: 'Nouveau modèle',
    body: 'Texte du message',
    variables: 'Variables disponibles',
    renderPreview: 'Aperçu avec des données fictives',
    missingVariables: 'Variables sans valeur',
    templateStatuses: {
      draft: 'Brouillon',
      pending: 'En validation',
      approved: 'Approuvé',
      rejected: 'Refusé',
    },
    templateName: 'Nom technique (a-z, 0-9, _)',
    readOnlyTemplate: 'Modèle approuvé ou en validation : lecture seule.',
  },
  imports: {
    title: 'Imports et exports',
    history: 'Historique des imports',
    newImport: 'Nouvel import',
    kind: 'Type de fichier',
    kinds: { contracts: 'Contrats et assurés', installments: 'Échéanciers', payments: 'Paiements' },
    file: 'Fichier CSV ou XLSX (5 Mo au plus)',
    upload: 'Téléverser',
    mapping: 'Correspondance des colonnes',
    field: 'Champ Beta Assur',
    column: 'Colonne du fichier',
    ignore: 'Ignorer',
    saveMapping: 'Mémoriser pour les prochains imports',
    validate: 'Vérifier les 20 premières lignes',
    missing: 'Champs obligatoires sans colonne',
    line: 'Ligne',
    errors: 'Erreurs',
    warnings: 'Avertissements',
    ok: 'Valide',
    dryRun: 'Lancer la simulation',
    dryRunRequired: 'Une simulation est obligatoire avant le premier import réel.',
    run: 'Lancer l’import réel',
    runConfirm: 'Lancer l’import réel ? Les données du fichier seront enregistrées.',
    markAbsent: 'Résilier les contrats en cours absents du fichier',
    progress: 'Progression',
    report: 'Télécharger le rapport d’erreurs',
    statuses: {
      uploaded: 'Téléversé',
      mapping: 'Correspondance',
      validating: 'Vérification',
      processing: 'En cours',
      completed: 'Terminé',
      failed: 'Échec',
    },
    simulation: 'Simulation',
    real: 'Réel',
    rows: 'Lignes',
    rowsOk: 'valides',
    rowsError: 'en erreur',
    rowsWarning: 'avec avertissement',
    summary: 'Résultat',
    createdAt: 'Créé le',
    exportsSoon: 'Les exports filtrés sont disponibles depuis chaque écran de liste.',
  },
  notice: {
    presets: { 30: '1 mois (30 jours)', 60: '2 mois (60 jours)', 90: '3 mois (90 jours)' },
    custom: 'Personnalisé',
    days: 'jours',
  },
  subscription: {
    tab: 'Abonnement',
    noPlan: 'Aucune offre',
    legacy:
      'Cabinet historique : aucune limite ni fonction retirée. Contactez Beta Suite pour choisir une offre.',
    statuses: {
      trial: 'Essai gratuit',
      active: 'Actif',
      past_due: 'Paiement en retard',
      suspended: 'Suspendu',
      cancelled: 'Résilié',
    },
    trialEnds: (date: string) =>
      `Essai gratuit jusqu’au ${date}, en mode brouillon. Choisissez une offre pour continuer ensuite.`,
    trialConfirmed: (plan: string, date: string) =>
      `Offre ${plan} choisie : l’abonnement démarrera le ${date}, à la fin de l’essai.`,
    suspended:
      'Abonnement suspendu : les envois réels sont arrêtés, vos données restent consultables. Choisissez une offre pour reprendre.',
    cancelled: 'Abonnement résilié : contactez Beta Suite pour le reprendre.',
    cancelScheduled: (date: string) => `Résiliation prévue le ${date}.`,
    renewsOn: (date: string) => `Prochain renouvellement le ${date}.`,
    cycleLabel: 'Facturation',
    cycles: { monthly: 'Mensuelle', annual: 'Annuelle' },
    nextCycle: (cycle: string) => `${cycle} au prochain renouvellement`,
    messagesThisMonth: 'Messages envoyés ce mois-ci (inclus)',
    limitNames: {
      users: 'Utilisateurs',
      activeContracts: 'Contrats en cours',
      insurers: 'Assureurs',
    },
    unlimited: 'illimité',
    overageHint: 'Au-delà des messages inclus, chaque message est facturé au tarif de l’offre.',
    plans: 'Offres',
    pricesHint: 'Prix hors taxes ; l’offre annuelle équivaut à dix mois.',
    perMonth: 'HT / mois',
    perYear: 'HT / an',
    includedMessages: (n: number) => `${n.toLocaleString('fr-FR')} messages inclus par mois`,
    features: {
      public_api: 'API publique pour votre SI',
      webhooks: 'Webhooks sortants',
      direct_debit: 'Prélèvement automatique',
      sso: 'Compte unique Beta Suite',
      sftp: 'Intégration SI (SFTP, connecteur)',
      llm_assist: 'Lecture assistée des réponses',
    },
    included: 'inclus',
    notIncluded: 'non inclus',
    current: 'Offre actuelle',
    choose: 'Choisir cette offre',
    keep: 'Changer de cycle',
    confirmTitle: (plan: string) => `Passer à l’offre ${plan}`,
    adjustmentDue: 'Régularisation ajoutée au prochain relevé :',
    adjustmentCredit: 'Crédit déduit du prochain relevé :',
    effectiveOn: 'Prise d’effet le',
    blocked: 'Votre usage dépasse les limites de cette offre :',
    cancel: 'Résilier l’abonnement',
    cancelMessage:
      'L’abonnement prendra fin à la fin de la période en cours. Vous pourrez annuler la résiliation d’ici là.',
    revertCancel: 'Annuler la résiliation',
    history: 'Historique',
    events: {
      created: 'Abonnement créé',
      confirmed: 'Offre choisie',
      started: 'Abonnement démarré',
      renewed: 'Renouvellement',
      plan_changed: 'Changement d’offre',
      cycle_changed: 'Changement de cycle',
      trial_expired: 'Fin de l’essai',
      cancel_requested: 'Résiliation demandée',
      cancel_reverted: 'Résiliation annulée',
      cancelled: 'Abonnement résilié',
      status_changed: 'Modifié par Beta Suite',
    },
  },
  signup: {
    cta: 'Pas encore client ? Créer l’espace de votre agence',
    title: 'Créer l’espace de votre agence',
    intro:
      'Essai gratuit de 30 jours, sans engagement. Beta Suite vérifie votre demande puis vous envoie l’invitation par email.',
    agencyName: 'Nom de l’agence',
    agentCode: 'Code d’agent général',
    optional: 'Facultatif',
    taxId: 'Matricule fiscal',
    taxIdHint: 'Exemple : 1234567A/M/000',
    city: 'Ville',
    insurers: 'Compagnies représentées',
    insurersHint: 'Séparées par des virgules',
    plan: 'Offre souhaitée',
    plans: {
      ESSENTIEL: 'Essentiel : agent indépendant',
      PRO: 'Pro : agence structurée',
      RESEAU: 'Réseau : grand cabinet',
    },
    adminName: 'Votre nom (administrateur)',
    phone: 'Téléphone',
    terms:
      'J’accepte les conditions générales d’utilisation et le traitement de ces informations pour l’ouverture de mon espace.',
    submit: 'Envoyer la demande',
    haveAccount: 'J’ai déjà un compte',
    received:
      'Merci, votre demande est enregistrée. Vous recevrez l’invitation par email dès son acceptation.',
  },
  billing: {
    title: 'Factures et paiement',
    method: 'Moyen de paiement',
    transfer: 'Virement',
    directDebit: 'Prélèvement automatique',
    mandatePending: 'mandat en attente de signature',
    transferHint:
      'Chaque facture est payable par virement sous 10 jours, en rappelant son numéro. Le prélèvement évite les oublis.',
    startDirectDebit: 'Mettre en place le prélèvement',
    stopDirectDebit: 'Arrêter le prélèvement',
    noInvoice: 'Aucune facture pour le moment.',
    number: 'Facture',
    period: 'Mois',
    total: 'Total TTC',
    dueOn: 'Échéance',
    status: 'Statut',
    statuses: { open: 'À payer', paid: 'Payée', void: 'Annulée' },
    overdue: 'En retard',
    download: 'Télécharger',
    simulationTitle: 'Signature du mandat de prélèvement (simulation)',
    simulationIntro:
      'Environnement de recette : aucun compte n’est prélevé. Choisissez l’issue de la signature.',
    sign: 'Signer le mandat',
    refuse: 'Refuser',
    banner: {
      past_due: 'Une facture d’abonnement est échue.',
      suspended:
        'Compte en lecture seule : abonnement suspendu, les envois aux assurés sont arrêtés.',
      cancelled: 'Abonnement résilié : compte en lecture seule.',
    },
    bannerAction: 'Régulariser',
    bannerContact: 'Contactez l’administrateur de votre cabinet.',
  },
  calls: {
    tab: 'Appels du jour',
    title: 'Appel à l’assuré',
    noPhone: 'Aucun numéro joignable : vérifier les coordonnées de l’assuré.',
    remindersSent: (n: number) => `${n} message(s) de relance envoyé(s) sur cette échéance.`,
    payments: (list: string) => `Paiements enregistrés : ${list}.`,
    noPayment: 'Aucun paiement enregistré sur cette échéance.',
    callBackAt: 'Rappel prévu le',
    outcome: 'Issue de l’appel',
    outcomes: {
      reached: 'Joint',
      promise: 'Promesse de paiement',
      refused: 'Refus de payer',
      no_answer: 'Pas de réponse',
      wrong_number: 'Mauvais numéro',
      already_paid: 'Dit avoir déjà payé',
    },
    promiseDate: 'Paiement promis le',
    callBack: 'Rappeler le',
    callBackHint: 'Vide : lendemain ouvré sans réponse, aucun rappel sinon.',
    report: 'Compte rendu de l’appel',
    save: 'Enregistrer l’appel',
  },
  pilotage: {
    title: 'Pilotage sur 12 mois',
    dueWithin: (days: number) => `À échéance sous ${days} jours`,
    installments: (n: number) => `${n} échéance(s) à encaisser`,
    renewalRate: 'Taux de renouvellement',
    renewalDetail: (yes: number, no: number) => `${yes} renouvelé(s), ${no} non renouvelé(s)`,
    collectedMonth: 'Primes encaissées ce mois',
    collectedYear: 'Depuis le 1er janvier',
    lost: 'Contrats perdus',
    since: (date: string) => `Depuis le ${date}`,
    byInsurer: 'À échéance sous 90 jours, par compagnie',
    count: 'Échéances',
    amount: 'Reste à encaisser',
    lostRecent: 'Derniers contrats perdus',
    noLost: 'Aucun contrat perdu sur la période.',
    reason: 'Motif',
  },
  settings: {
    notices: 'Préavis et documents',
    dueNoticeDays: 'Préavis avant échéance',
    dueNoticeHint:
      'Les étapes des scénarios comptées depuis le préavis partent ce nombre de jours avant chaque échéance.',
    contractEndNoticeDays: 'Rappel avant la fin du contrat',
    contractEndHint:
      'Message à l’assuré et dossier de renouvellement dans la file de travail (contrats sans tacite reconduction).',
    noContractEndNotice: 'Pas de rappel',
    attestationAfterPayment:
      'Envoyer l’attestation de paiement à l’assuré dès que la période en cours est réglée',
    attestationAfterPaymentShort: 'Attestation envoyée après paiement',
    inherited: 'Réglage du cabinet',
    inheritedHint: 'En jours ; vide : réglage du cabinet.',
    title: 'Paramètres',
    tenant: 'Cabinet',
    insurers: 'Assureurs',
    productLines: 'Lignes de produit',
    agents: 'Agents',
    users: 'Utilisateurs',
    security: 'Ma sécurité',
    name: 'Nom',
    timezone: 'Fuseau horaire',
    localeDefault: 'Langue par défaut',
    sendWindow: 'Fenêtre d’envoi',
    sendDays: 'Jours d’envoi',
    dailyCap: 'Plafond quotidien de messages',
    noCap: 'Sans plafond',
    logo: 'Logo',
    uploadLogo: 'Téléverser (PNG ou JPEG, 200 Ko)',
    options: 'Options',
    optionLabels: {
      llmClassification:
        'Lecture assistée des réponses libres : un modèle de langage propose une intention (texte anonymisé, décision au gestionnaire)',
      monthlyReportEmail: 'Envoyer le rapport mensuel de recouvrement aux administrateurs',
      allowInternationalPhones: 'Accepter les numéros étrangers à l’import',
      assignEscalationsToAgents:
        'Confier les escalades et les appels à l’agent du contrat plutôt qu’à l’équipe',
    },
    days: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'],
    sending: 'Mode d’envoi',
    draftOn: 'Mode brouillon actif : les relances sont simulées.',
    draftOff: 'Envoi réel actif : les relances partent vers les assurés.',
    activateSending: 'Activer l’envoi réel',
    activateConfirm: 'Saisissez ACTIVER pour confirmer l’envoi réel des relances.',
    backToDraft: 'Revenir au mode brouillon',
    holidays: 'Jours non travaillés',
    holidayDate: 'Date',
    holidayLabel: 'Libellé',
    addHoliday: 'Ajouter un jour',
    holidayScopes: { national: 'National', tenant: 'Cabinet' },
    holidayKinds: { fixed: 'Fixe', estimated: 'Estimé', confirmed: 'Confirmé' },
    code: 'Code',
    legalName: 'Raison sociale',
    displayName: 'Nom affiché',
    senderName: 'Expéditeur SMS (11 caractères)',
    legalMentions: 'Mentions légales',
    labelFr: 'Libellé (FR)',
    labelAr: 'Libellé (AR)',
    family: 'Famille',
    defaultPeriodicity: 'Périodicité par défaut',
    graceDays: 'Délai de grâce (jours)',
    suspensionDays: 'Suspension après (jours)',
    noSuspension: 'Jamais',
    branch: 'Agence',
    phone: 'Téléphone',
    email: 'Email',
    linkedUser: 'Utilisateur lié',
    role: 'Rôle',
    status: 'Statut',
    userStatuses: {
      invited: 'Invité',
      active: 'Actif',
      locked: 'Verrouillé',
      disabled: 'Désactivé',
    },
    totp: 'TOTP',
    lastLogin: 'Dernière connexion',
    invite: 'Inviter un utilisateur',
    invitationLink: 'Lien d’invitation à transmettre à la personne invitée',
    disable: 'Désactiver',
    enable: 'Réactiver',
    disableConfirm: 'Désactiver ce compte ? Ses sessions seront fermées.',
    totpTitle: 'Double authentification (TOTP)',
    totpEnabled: 'Activée',
    totpDisabled: 'Désactivée',
    totpStart: 'Activer la double authentification',
    totpScan:
      'Ajoutez ce compte dans votre application d’authentification, puis saisissez le code affiché.',
    totpSecret: 'Clé secrète',
    totpCode: 'Code à 6 chiffres',
    totpConfirm: 'Confirmer',
    totpDisable: 'Désactiver la double authentification',
  },
  worklist: {
    title: 'File de travail',
    mine: 'À moi',
    team: 'Équipe (non assignées)',
    all: 'Toutes',
    closed: 'Closes',
    sortAge: 'Les plus anciennes',
    sortAmount: 'Les plus gros montants',
    kind: 'Nature',
    kinds: {
      unpaid: 'Impayé',
      optout: 'Opposition',
      payment_check: 'Paiement à vérifier',
      question: 'Question',
      overpayment: 'Trop-perçu',
      refund: 'Remboursement',
      delivery: 'Envoi impossible',
      renewal: 'Renouvellement',
      claim: 'Sinistre déclaré',
    },
    statuses: { open: 'Ouverte', in_progress: 'En cours', closed: 'Close' },
    openedAt: 'Ouverte le',
    remaining: 'Reste dû',
    assigned: 'Assignée à',
    nobody: 'Équipe',
    me: 'moi',
    empty: 'Aucune escalade à traiter.',
    inbound: 'Message de l’assuré',
    suggestion: 'Lecture suggérée (à vérifier)',
    intents: {
      paid_elsewhere: 'Déjà payé',
      stop: 'Ne plus recevoir de messages',
      later: 'Demande de délai',
      question: 'Question',
      other: 'Autre',
    },
    detail: 'Détails',
    notes: 'Historique du traitement',
    messages: 'Derniers envois',
    take: 'Prendre en charge',
    note: 'Ajouter une note',
    noteLabel: 'Note',
    call: 'Journaliser un appel',
    markPaid: 'Marquer payé',
    grantDelay: 'Accorder un délai',
    days: 'Jours',
    cancelContract: 'Résilier le contrat',
    cancelConfirm: 'Résilier ce contrat ? Les échéances à venir seront annulées.',
    close: 'Clore',
    resolution: 'Résolution',
    reassign: 'Réassigner',
    actions: {
      take: 'Prise en charge',
      note: 'Note',
      call: 'Appel',
      mark_paid: 'Payé',
      grant_delay: 'Délai accordé',
      cancel_contract: 'Résiliation',
      close: 'Clôture',
      assign: 'Réassignation',
    } as Record<string, string>,
    open: 'Ouvrir',
  },
  payments: {
    record: 'Enregistrer un paiement',
    amount: 'Montant (DT)',
    method: 'Mode de paiement',
    paidAt: 'Date du paiement',
    reference: 'Référence',
    issueReceipt: 'Envoyer un reçu Beta Assur à l’assuré',
    recorded: 'Paiement enregistré.',
    overpaid: 'Excédent signalé dans la file de travail',
    sendNow: 'Envoyer maintenant l’étape en cours',
    sendNowConfirm: 'Envoyer tout de suite l’étape en cours de la relance (hors planning) ?',
    sent: 'Envoi demandé.',
    payLink: 'Lien de la page assuré',
    showPayLink: 'Afficher le lien de paiement',
  },
  bulk: {
    selected: { one: '{n} sélectionnée', other: '{n} sélectionnées' } as Plural,
    selectAll: 'Tout sélectionner',
    select: 'Sélectionner',
    action: 'Action de masse',
    actions: {
      start: 'Lancer la relance',
      pause: 'Mettre en pause',
      resume: 'Reprendre',
      stop: 'Arrêter la relance',
      change_scenario: 'Changer de scénario',
      mark_paid: 'Marquer payé',
    },
    scenario: 'Scénario',
    preview: 'Voir le récapitulatif',
    recap: 'Récapitulatif',
    applicable: { one: '{n} échéance concernée', other: '{n} échéances concernées' } as Plural,
    skipped: { one: '{n} ignorée', other: '{n} ignorées' } as Plural,
    apply: 'Appliquer',
    applied: { one: '{n} échéance traitée', other: '{n} échéances traitées' } as Plural,
  },
  rights: {
    title: 'Opposition et droits',
    optOutChannels: 'Canaux refusés par l’assuré',
    saveOptOut: 'Enregistrer l’opposition',
    verifyPhone: 'Vérifier le numéro',
    sendCode: 'Envoyer un code par SMS',
    codeSent: 'Code envoyé. Saisissez le code communiqué par l’assuré.',
    code: 'Code reçu par l’assuré',
    confirmCode: 'Valider',
    verified: 'Numéro vérifié',
    export: 'Exporter les données (JSON)',
    anonymize: 'Anonymiser l’assuré',
    anonymizeConfirm:
      'Saisissez ANONYMISER : identité et contacts seront effacés définitivement, l’historique financier est conservé.',
    proof: 'Preuve d’envoi (PDF)',
  },
  integrations: {
    title: 'Messagerie et paiement',
    connect: 'Messagerie (SMS, WhatsApp, email)',
    kashy: 'Kashy (paiement en ligne)',
    configured: 'Active',
    missing: 'En cours d’activation par Beta Suite',
    account: 'Compte',
    suspended: 'Envois suspendus par sécurité',
    suspendedHint:
      'Trop d’échecs d’envoi récents. Vérifiez les numéros et les modèles, puis réactivez l’envoi réel.',
  },
  templateReview: {
    submit: 'Soumettre à l’approbation WhatsApp',
    sync: 'Actualiser le statut',
    submitted: 'Modèle soumis pour approbation.',
    reviewNote: 'Motif du refus',
  },
  mfa: {
    required:
      'Activez la double authentification pour accéder au portail (obligatoire pour les administrateurs).',
  },
  onboarding: {
    title: 'Démarrage du cabinet',
    sandbox: 'Tenant de démonstration : aucun message ni paiement réel.',
    done: 'fait',
    todo: 'à faire',
    steps: {
      insurer: 'Créer au moins un assureur',
      products: 'Créer les lignes de produit',
      users: 'Inviter l’équipe',
      portfolio: 'Importer le portefeuille',
      scenario: 'Publier un scénario',
      templates: 'Faire approuver les modèles WhatsApp',
      messaging: 'Messagerie activée par Beta Suite',
      live: 'Quitter le mode brouillon',
    },
  },
  connectors: {
    tab: 'Connecteur SI',
    intro:
      'Beta Assur lit votre système d’information à intervalle régulier, en lecture seule : aucune donnée n’est jamais écrite chez vous. Chaque lecture passe par les mêmes contrôles que l’import de fichiers.',
    url: 'Adresse de la source',
    urlHint: 'API JSON ou fichier CSV publié, en HTTPS.',
    format: 'Format',
    formats: { json: 'API JSON', csv: 'Fichier CSV' },
    interval: 'Fréquence de lecture',
    intervals: {
      15: 'Toutes les 15 minutes',
      60: 'Toutes les heures',
      360: 'Toutes les 6 heures',
      1440: 'Une fois par jour',
    },
    auth: 'Accès',
    auths: {
      none: 'Sans authentification',
      bearer: 'Jeton (Bearer)',
      basic: 'Identifiant et mot de passe',
      header: 'Clé dans un en-tête',
    },
    headerName: 'Nom de l’en-tête',
    secret: 'Jeton ou clé',
    secretBasic: 'identifiant:mot de passe',
    secretHint: 'Conservé chiffré, jamais réaffiché.',
    secretKept: 'Laisser vide pour conserver l’accès enregistré.',
    itemsPath: 'Chemin des enregistrements',
    itemsPathHint: 'Vide si la réponse est directement un tableau.',
    sinceParam: 'Paramètre « modifié depuis »',
    sinceParamHint: 'Pour ne lire que les changements depuis la dernière lecture réussie.',
    create: 'Déclarer la source',
    save: 'Enregistrer',
    edit: 'Modifier',
    remove: 'Supprimer le connecteur',
    removeMessage: 'Supprimer ce connecteur ? Les données déjà importées sont conservées.',
    activate: 'Lecture planifiée active',
    enabled: 'Actif',
    disabled: 'Inactif',
    test: 'Lecture d’essai',
    runNow: 'Lire maintenant',
    queued: 'Lecture programmée : le résultat apparaît dans le journal.',
    testResult: (rows: number, status: number) =>
      `Lecture d’essai (HTTP ${status}) : ${rows} enregistrement(s), rien n’a été importé.`,
    saveMapping: 'Enregistrer la correspondance',
    mappingSaved: 'Correspondance enregistrée.',
    missing:
      'Correspondance incomplète : faites une lecture d’essai et associez les champs obligatoires.',
    lastRun: 'Dernière lecture',
    never: 'Jamais',
    journal: 'Journal des lectures',
    noRuns: 'Aucune lecture pour le moment.',
    runAt: 'Date',
    trigger: 'Origine',
    triggers: { schedule: 'Planifiée', manual: 'Manuelle' },
    status: 'Résultat',
    statuses: { ok: 'Importé', empty: 'Rien de nouveau', error: 'Erreur' },
    rows: 'Enregistrements',
    detail: 'Détail',
    batch: 'Voir l’import',
  },
  developers: {
    title: 'Développeurs',
    keys: 'Clés d’API',
    keyLabel: 'Libellé',
    scopes: 'Droits',
    scopeLabels: {
      read: 'Lecture',
      'contracts:write': 'Contrats',
      'installments:write': 'Échéances',
      'payments:write': 'Paiements',
    },
    createKey: 'Créer la clé',
    newKey: 'Nouvelle clé : copiez-la maintenant.',
    newSecret: 'Secret de signature : copiez-le maintenant.',
    shownOnce: 'Elle ne sera plus affichée. Seule son empreinte est conservée.',
    prefix: 'Préfixe',
    lastUsed: 'Dernier appel',
    never: 'Jamais',
    revoked: 'Révoquée',
    revoke: 'Révoquer',
    revokeMessage: 'Les appels avec cette clé seront refusés immédiatement.',
    example: 'Exemple d’appel',
    docs: 'Documentation de l’API publique :',
    requests: 'Journal des appels',
    webhooks: 'Webhooks sortants',
    url: 'Adresse HTTPS de réception',
    events: 'Événements',
    addWebhook: 'Ajouter le webhook',
    active: 'Actif',
    deleteWebhook: 'Plus aucun événement ne sera envoyé à cette adresse.',
    deliveries: 'Livraisons',
    deliveryStatuses: {
      pending: 'En attente',
      delivered: 'Livré',
      failed: 'Nouvel essai prévu',
      undelivered: 'Non délivré',
    },
    attempts: { one: '{n} essai', other: '{n} essais' } as Plural,
    replay: 'Renvoyer',
  },
  reports: {
    title: 'Rapports',
    kinds: {
      recovery: 'Recouvrement',
      funnel: 'Entonnoir',
      scenarios: 'Scénarios',
      channels: 'Canaux',
      quality: 'Qualité des données',
      worklist: 'File de travail',
      consumption: 'Consommation',
      preferredChannels: 'Canal préféré',
    },
    summary: 'Synthèse',
    previous: 'Période précédente',
    insurer: 'Assureur',
    periodLabel: 'Période :',
    exportCsv: 'Exporter en CSV',
    exportXlsx: 'Exporter en Excel',
    exports: 'Exports des listes',
    exportKinds: {
      installments: 'Échéances',
      payments: 'Paiements',
      messages: 'Messages',
      policyholders: 'Assurés',
    },
    monthly: 'Rapports mensuels de recouvrement',
    noMonthly: 'Le premier rapport mensuel sera déposé le 1er du mois prochain.',
    download: 'Télécharger',
  },
};

export type Messages = typeof fr;
